/**
 * Screen 2 — the prospect's details, and a recap of the plan from screen 1.
 *
 * Nothing is sent anywhere from here. The details are held in this tab's
 * memory (see context.tsx) for the trial to greet them and for the payment
 * screen to submit. No account exists on the server until the down payment
 * clears; the real submission is made from pages/payment.
 *
 * Two ways forward, both valid: try the product on a real job description
 * first, or skip the trial and go straight to payment. The trial is the
 * default because it is the point of the flow, but a prospect who has already
 * decided should not be made to sit through it.
 */

import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Form, Input } from 'antd';
import FlowShell from '../../components/flow-shell';
import { useSelfServe, AccountDetails } from '../../context';
import { formatUsd } from '../../utils/quote';
import styles from './index.module.css';

type Next = 'trial' | 'payment';

const Account = () => {
  const navigate = useNavigate();
  const { quote, account, completeAccount } = useSelfServe();
  const [form] = Form.useForm<AccountDetails>();
  // Which button submitted the form. Both run the same validation; only the
  // destination differs.
  const next = useRef<Next>('trial');

  const onFinish = (values: AccountDetails) => {
    completeAccount({
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      email: values.email.trim(),
      company: values.company.trim(),
    });
    navigate(`/selfserve/${next.current}`);
  };

  const submitTo = (destination: Next) => {
    next.current = destination;
    form.submit();
  };

  if (!quote) return null;

  return (
    <FlowShell
      step={2}
      title="Your details"
      subtitle="About thirty seconds. Nothing is charged until you choose to pay."
    >
      <div className={styles.recap}>
        <h2 className={styles.recapTitle}>Your plan</h2>
        <dl className={styles.recapFigures}>
          <div>
            <dt>Hires a year</dt>
            <dd>{quote.hires.toLocaleString('en-US')}</dd>
          </div>
          <div>
            <dt>Rate per hire</dt>
            <dd>{formatUsd(quote.ratePerHire)}</dd>
          </div>
          <div>
            <dt>Contract value</dt>
            <dd>{formatUsd(quote.contractValue)}</dd>
          </div>
        </dl>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        requiredMark={false}
        className={styles.form}
        // Coming back from the trial or payment screen keeps what was typed.
        initialValues={account ?? undefined}
      >
        <div className={styles.nameRow}>
          <Form.Item
            name="firstName"
            label="First name"
            rules={[{ required: true, message: 'Please add your first name.' }]}
          >
            <Input placeholder="Jordan" autoComplete="given-name" />
          </Form.Item>

          <Form.Item
            name="lastName"
            label="Last name"
            rules={[{ required: true, message: 'Please add your last name.' }]}
          >
            <Input placeholder="Rivera" autoComplete="family-name" />
          </Form.Item>
        </div>

        <Form.Item
          name="email"
          label="Work email"
          rules={[
            { required: true, message: 'Please add your work email.' },
            {
              type: 'email',
              message: 'That does not look like an email address.',
            },
          ]}
        >
          <Input placeholder="jordan@company.com" autoComplete="email" />
        </Form.Item>

        <Form.Item
          name="company"
          label="Company"
          rules={[{ required: true, message: 'Please add your company name.' }]}
        >
          <Input placeholder="Company name" autoComplete="organization" />
        </Form.Item>

        <p className={styles.whatHappens}>
          Next, try Avrio on one of your own job descriptions. Nothing is
          charged for the trial. If you have already decided, you can skip it
          and go straight to payment.
        </p>

        <div className={styles.actions}>
          <Button
            type="primary"
            size="large"
            className={styles.cta}
            onClick={() => submitTo('trial')}
          >
            Try it on a job description
          </Button>
          <Button
            size="large"
            className={styles.cta}
            onClick={() => submitTo('payment')}
          >
            Skip the trial and pay now
          </Button>
        </div>
      </Form>
    </FlowShell>
  );
};

export default Account;
